import type { dayOfWeek } from "types/app";

export type ShareableScheduleConfig = {
  /** Full plan id, e.g. "half-intermediate" */
  planId: string;
  /** ISO string for race date (plan end date), e.g. "2026-02-26T00:00:00.000Z" */
  raceDateIso: string;
  /** "km" | "mi" */
  units?: "km" | "mi";
  /** weekStartsOn numeric value used by use-query-params (0/1/6) */
  weekStartsOn?: 0 | 1 | 6;
  /** Optional schedule customization */
  trainingDays?: dayOfWeek[];
  longRunDay?: dayOfWeek | "";
};

const DAY_ABBR: Record<dayOfWeek, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const ABBR_DAY: Record<string, dayOfWeek> = Object.fromEntries(
  Object.entries(DAY_ABBR).map(([k, v]) => [v, k as dayOfWeek]),
) as Record<string, dayOfWeek>;

export function encodeTrainingDays(
  days: dayOfWeek[] | undefined,
): string | null {
  if (!days || days.length === 0) return null;
  const uniq = Array.from(new Set(days));
  // stable, human-readable-ish order
  const order: dayOfWeek[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const sorted = uniq
    .slice()
    .sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return sorted.map((d) => DAY_ABBR[d]).join(",");
}

export function decodeTrainingDays(
  value: string | null | undefined,
): dayOfWeek[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((abbr) => ABBR_DAY[abbr])
    .filter((d): d is dayOfWeek => Boolean(d));
}

export function encodeLongRunDay(
  day: dayOfWeek | "" | undefined,
): string | null {
  if (!day) return null;
  return DAY_ABBR[day];
}

export function decodeLongRunDay(
  value: string | null | undefined,
): dayOfWeek | "" {
  if (!value) return "";
  return ABBR_DAY[value] ?? "";
}
